import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AppShell from "../../components/layout/AppShell";
import StatCard from "../../components/ui/StatCard";
import { Table, Select, Input, Empty, Alert } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import Badge from "../../components/ui/Badge";
import { getTransactions } from "../../services/api";
import { getTransactionAnalytics } from "../../services/analytics.api";
import useAnalytics from "../../hooks/useAnalytics";
import AnalyticsSummaryCards from "../../components/ui/AnalyticsSummaryCards";
import DateFilter from "../../components/ui/DateFilter";
import { formatAmount, getMaxDecimalsForCurrency, getDateFilterRange } from "../../utils";
import { TRANSACTIONS_ANALYTICS_CONFIG } from "../../config/transactionAnalytics.config";
import { DEFAULT_PAGE_SIZE_OPTIONS, TRANSACTION_STATUS_OPTIONS, DATE_FILTER_OPTIONS } from "../../utils/constants";

// Kill-switch for this page's analytics <DateFilter />.
const DISABLE_DATE_FILTER_FOR_ANALYTICS = false;

// Kill-switch for this page's transactions-table <DateFilter />.
const DISABLE_DATE_FILTER_FOR_TRANSACTIONS = false;

const fmtAmount = (amount, currency = "USD") => {
  const n = Number(amount);
  if (isNaN(n)) return "—";
  const ccy = (currency || "USD").toUpperCase();
  return `${ccy} ${formatAmount(amount,getMaxDecimalsForCurrency(ccy))}`;
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Transactions = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);
  const quotePermissions = useSelector((s) => s.auth.quotePermissions);
  const hasWallet = quotePermissions.canOfframp || quotePermissions.canOnramp;

  const [txns, setTxns] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(search, 400);

  const [analyticsDateRange, setAnalyticsDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );
  const [txnDateRange, setTxnDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );
  const { value: analytics, loading: analyticsLoading } = useAnalytics(
    getTransactionAnalytics,
    DISABLE_DATE_FILTER_FOR_ANALYTICS
      ? {}
      : { from: analyticsDateRange.startDate, to: analyticsDateRange.endDate }
  );

  const buildAnalyticsItems = (bucket) =>
    TRANSACTIONS_ANALYTICS_CONFIG.map(({ key, title, valueClassName }) => {
      const data = bucket?.[key]?.data || [];
      const count = bucket?.[key]?.count;
      if (!count) return null;
      return {
        title,
        value: (
          <div className="flex flex-col gap-0.5">
            {(data).map((d) => (
              <span key={d.currency}>{fmtAmount(d.amount, d.currency)}</span>
            ))}
          </div>
        ),
        subLabel: `${count ?? 0} Transactions`,
        valueClassName,
      };
    }).filter(Boolean);

  const analyticsItems = (analytics != null) ? buildAnalyticsItems(analytics?.life) : [];

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError("");
    getTransactions({
      userId,
      page,
      limit: pageSize,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(debouncedSearch ? { searchQuery: debouncedSearch } : {}),
      ...(!DISABLE_DATE_FILTER_FOR_TRANSACTIONS && txnDateRange.startDate ? { from: txnDateRange.startDate } : {}),
      ...(!DISABLE_DATE_FILTER_FOR_TRANSACTIONS && txnDateRange.endDate ? { to: txnDateRange.endDate } : {}),
    })
      .then((res) => {
        setTxns(res.data || []);
        setMeta(res.meta || {});
      })
      .catch(() => {
        setTxns([]);
        setError("Failed to load transactions. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [userId, page, pageSize, statusFilter, debouncedSearch, txnDateRange]);

  const changeTxnDateFilter = (_filter, range) => {
    setTxnDateRange((prev) =>
      prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
    );
    setPage(1);
  };

  const columnStructure = [
    {
      title: "SN",
      key: "sn",
      width: 60,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Invoice ID",
      key: "invoice_id",
      render: (_, t) => t.paymentRequest?.invoiceId || "—",
    },
    {
      title: "Sender",
      key: "sender",
      render: (_, t) => t.fromDetails?.name || "—",
    },
    {
      title: "Date",
      key: "date",
      render: (_, t) => (
        <span className="whitespace-nowrap text-sm text-gray-500">{formatDate(t.createdAt)}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, t) => (
        <div className="whitespace-nowrap">
          <Badge status={t.status?.toUpperCase() || "PENDING"} />
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, t) => (
        <span className="text-sm font-medium text-gray-900 tabular-nums">
          {formatAmount(t.quote?.toAmount, getMaxDecimalsForCurrency(t.quote?.toCurrency))}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      width: 100,
      render: (_, t) => (
        <button
          type="button"
          onClick={() => navigate(`/receiver/transactions/${t.transactionId || t.id}`)}
          className="inline-flex items-center justify-center gap-1.5 p-1.5 text-sm rounded-lg text-blue-600 hover:text-blue-800 transition-colors cursor-pointer border-0 bg-transparent"
        >
          View <ArrowRightOutlined />
        </button>
      ),
    },
  ];

  return (
    <AppShell>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          All payments received against your invoices.
        </p>
      </div>

      {/* Analytics summary */}
      {analyticsItems.length > 0 && (
        <AnalyticsSummaryCards
          items={analyticsItems}
          loading={analyticsLoading}
          showDateFilter={!DISABLE_DATE_FILTER_FOR_ANALYTICS}
          onDateFilterChange={(_filter, range) =>
            setAnalyticsDateRange((prev) =>
              prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
            )
          }
        />)}

      {/* Stat cards */}
      <div className={`grid gap-4 mb-6 ${hasWallet ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-2"}`}>
        {/* Issue 5: Hide "Total Withdrawn" for ONRAMP_OFFRAMP users */}
        {hasWallet && (
          <StatCard
            label="Total Withdrawn"
            value="—"
          />
        )}
      </div>

      {/* Transactions table */}
      {error && <Alert type="error" message={error} showIcon className="mb-4" />}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-800 m-0">Your Transactions</h2>
            {meta.total > 0 && (
              <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full border border-gray-200">
                {meta.total.toLocaleString()} total
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input.Search
              placeholder="Search by Invoice ID or…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className="w-60"
            />
            {!DISABLE_DATE_FILTER_FOR_TRANSACTIONS && (
              <DateFilter onChange={changeTxnDateFilter} />
            )}
            <Select
              value={statusFilter || undefined}
              placeholder="All Statuses"
              allowClear
              showSearch={false}
              onChange={(val) => {
                setStatusFilter(val || "");
                setPage(1);
              }}
              className="min-w-[150px]"
              options={[
                { value: "", label: "All Statuses" },
                ...Object.values(TRANSACTION_STATUS_OPTIONS).map((s) => ({
                  value: s,
                  label: <span className="capitalize">{s}</span>,
                })),
              ]}
            />
          </div>
        </div>

        <Table
          className="admin-table table-blue-zebra"
          columns={columnStructure}
          dataSource={txns}
          rowKey={(t) => t.transactionId || t.id}
          loading={loading}
          locale={{
            emptyText: loading ? (
              <div className="py-12" />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Transactions will appear here once invoices are paid."
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total: meta.total || 0,
            showSizeChanger: { showSearch: false },
            showQuickJumper: false,
            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS.map(String),
            responsive: true,
            onShowSizeChange: (_current, newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              if (newPageSize && newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setPage(1);
              }
            },
            showTotal: (totalCount, range) => (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Showing{" "}
                <span className="font-medium text-gray-800">
                  {range[0]}-{range[1]}
                </span>{" "}
                of <span className="font-medium text-gray-800">{totalCount}</span> transactions
              </span>
            ),
          }}
        />
      </div>
    </AppShell>
  );
};

export default Transactions;
